import {
  useMutation,
  useInfiniteQuery,
  useQuery,
  useQueryClient,
} from "@tanstack/react-query";
import { AxiosResponse } from "axios";
import { ApiResponse } from "@src/types/httpApi";
import {
  createInvoice,
  fetchInvoices,
  fetchMyInvoices,
  updateInvoice,
  submitInvoice,
  fetchInvoiceById,
} from "@src/apis/invoiceApi";
import { IInvoice, CreateInvoicePayload } from "@src/types/invoice";

export const useInfiniteInvoices = (options?: any) => {
  return useInfiniteQuery<AxiosResponse<ApiResponse<IInvoice[]>>>({
    queryKey: ["invoices"],
    queryFn: ({ pageParam = 1 }: { pageParam?: number }) =>
      fetchInvoices(pageParam),
    getNextPageParam: (
      lastPage: AxiosResponse<ApiResponse<IInvoice[]>> | undefined
    ) => {
      if (!lastPage || !lastPage?.data?.meta?.pagination) return undefined;
      const { page, pageCount } = lastPage.data.meta.pagination;
      return page < pageCount ? page + 1 : undefined;
    },
    ...options,
  });
};

export const useInfiniteMyInvoices = (curatorId?: string, options?: any) => {
  return useInfiniteQuery<AxiosResponse<ApiResponse<IInvoice[]>>>({
    queryKey: ["invoices", "mine", curatorId],
    queryFn: ({ pageParam = 1 }: { pageParam?: number }) => {
      if (!curatorId) return Promise.resolve({ data: { data: [] } } as any);
      return fetchMyInvoices(curatorId, pageParam);
    },
    getNextPageParam: (
      lastPage: AxiosResponse<ApiResponse<IInvoice[]>> | undefined
    ) => {
      if (!lastPage || !lastPage?.data?.meta?.pagination) return undefined;
      const { page, pageCount } = lastPage.data.meta.pagination;
      return page < pageCount ? page + 1 : undefined;
    },
    enabled: !!curatorId && options?.enabled !== false,
    ...options,
  });
};

export const useCreateInvoice = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (payload: CreateInvoicePayload) => createInvoice(payload),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["invoices"] });
    },
  });
};

export const useUpdateInvoice = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({
      documentId,
      payload,
    }: {
      documentId: string;
      payload: Partial<IInvoice>;
    }) => updateInvoice(documentId, payload),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["invoices"] });
    },
  });
};

export const useSubmitInvoice = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (documentId: string) => submitInvoice(documentId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["invoices"] });
    },
  });
};

export const useInvoiceDetail = (documentId: string, options?: any) => {
  return useQuery<AxiosResponse<{ data: IInvoice }>>({
    queryKey: ["invoices", "detail", documentId],
    queryFn: () => fetchInvoiceById(documentId),
    enabled: !!documentId,
    ...options,
  });
};
